/**
 * Estimates how long the spoken audio will be when ElevenLabs reads the
 * script. Used in the UI to warn the user before generation when the script
 * obviously overruns (or under-runs) the video length.
 *
 * Heuristic — calibrated for natural Polish/English UGC tempo:
 *  - average pace 2.4 words/second (~144 WPM)
 *  - inter-beat pause 0.4s (matches AudioMixerService.INTER_BEAT_PAUSE_S)
 *  - empty beats contribute 0
 */
import type { Script } from "./api";

const WORDS_PER_SECOND = 2.4;
const INTER_BEAT_PAUSE_S = 0.4;

export function estimateScriptDurationSeconds(script: Script): number {
  const nonEmpty = script.filter((b) => b.text.trim().length > 0);
  if (nonEmpty.length === 0) return 0;
  const wordCount = nonEmpty.reduce((sum, b) => sum + b.text.trim().split(/\s+/).length, 0);
  const speech = wordCount / WORDS_PER_SECOND;
  const pauses = (nonEmpty.length - 1) * INTER_BEAT_PAUSE_S;
  return speech + pauses;
}

export interface ScriptDurationFit {
  estimatedSeconds: number;
  videoSeconds: number;
  status: "empty" | "fits" | "overrun" | "underrun";
  /** Polish-language hint to show the user. */
  hint: string;
}

const UNDERRUN_RATIO = 0.5;

export function evaluateScriptFit(script: Script, videoSeconds: number): ScriptDurationFit {
  const estimatedSeconds = estimateScriptDurationSeconds(script);
  if (estimatedSeconds === 0) {
    return {
      estimatedSeconds,
      videoSeconds,
      status: "empty",
      hint: "Brak kwestii — wygeneruj propozycję lub wpisz tekst.",
    };
  }
  if (estimatedSeconds > videoSeconds + 0.3) {
    return {
      estimatedSeconds,
      videoSeconds,
      status: "overrun",
      hint: `Skrypt jest za długi: ~${formatSeconds(estimatedSeconds)} mowy w ${videoSeconds}s wideo. Skróć tekst albo wybierz dłuższy wariant.`,
    };
  }
  if (estimatedSeconds < videoSeconds * UNDERRUN_RATIO) {
    return {
      estimatedSeconds,
      videoSeconds,
      status: "underrun",
      hint: `Skrypt jest krótki: ~${formatSeconds(estimatedSeconds)} mowy na ${videoSeconds}s wideo. Reszta będzie ciszą.`,
    };
  }
  return {
    estimatedSeconds,
    videoSeconds,
    status: "fits",
    hint: `Szacowana długość mowy: ~${formatSeconds(estimatedSeconds)} (mieści się w ${videoSeconds}s).`,
  };
}

function formatSeconds(seconds: number): string {
  return `${seconds.toFixed(1)}s`;
}
